import { useState, useRef, useCallback } from 'react';
import ddt from '@wix/da-ddt';
import { PapiAttachmentFile } from '@wix/da-papi-types';
import { ADDITIONAL_MEDIA_ID_PREFIX } from '../constants';
import { hasWindow } from '../../utils/browser';
import { ImageSource } from '../../DeviationViews/Full/types';

const logger = ddt.createLogger('multifile');

type OptionalImageSource = ImageSource | undefined;

/** A useState whose setter is wrapped to have the side-effect of updating the current url
 *  when the viewed image is changed, adding a hash fragment/deep link to that image */
export function useMultiArtCurrentImageTracking(
  mainImage: OptionalImageSource,
  additionalMedia: PapiAttachmentFile[]
): ReturnType<typeof useState<OptionalImageSource>> {
  const [currentlyViewedImageSource, originalSetter] = useState<OptionalImageSource>(mainImage);

  const initialRender = useRef(true);
  const setCurrentlyViewedImageSource = useCallback(
    (source: OptionalImageSource) => {
      const indexIntoImages = [mainImage, ...additionalMedia].findIndex(s => s === source);
      logger.log(
        'useMultiArtCurrentImageTracking, source:',
        source?.['title'] ?? source?.['filename'],
        'index:',
        indexIntoImages
      );
      if (indexIntoImages >= 0) {
        const returned = originalSetter(source);
        // don't do this on the initial render or we'll overwrite a deeplink -into- the deviation
        // with the mainImage in the useState above
        if (!initialRender.current) {
          updateUrlWithDeepLink(indexIntoImages);
        } else {
          initialRender.current = false;
        }
        return returned;
      }
    },

    [mainImage, additionalMedia, originalSetter]
  );

  return [currentlyViewedImageSource, setCurrentlyViewedImageSource];
}

function updateUrlWithDeepLink(index: number) {
  if (!hasWindow()) {
    return;
  }
  logger.log('useMultiArtCurrentImageTracking, index:', index);

  const newUrl = new URL(window.location.href);
  newUrl.hash = `${ADDITIONAL_MEDIA_ID_PREFIX}${index + 1}`;
  history.replaceState(null, '', newUrl);
}
